import { sdk } from '../sdk';
import { getErrorMessage } from '../utils/errorMessage';

export interface Controller {
  _id: string;
  name: string;
  cnic: string;
  phone: string;
  fatherName: string;
  address: string;
  createdAt: string;
  updatedAt: string;
}

export interface ControllersResponse {
  success: boolean;
  data: Controller[];
  message: string;
}

export interface ControllerResponse {
  success: boolean;
  data: Controller;
  message: string;
}

export interface CreateControllerData {
  name: string;
  cnic: string;
  phone: string;
  fatherName: string;
  address: string;
}

export interface UpdateControllerData extends Partial<CreateControllerData> {}

export class ControllersAPI {
  static async getAllControllers(params?: { page?: number; limit?: number; search?: string }): Promise<{ data: Controller[]; pagination?: { page: number; limit: number; total: number; totalPages: number } }> {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.search) queryParams.append('search', params.search);
      
      const url = `/api/v1/controllers${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await sdk.client.get(url);
      
      // If response is empty or undefined, return empty array
      if (!response) {
        return { data: [] };
      }
      
      // Check if the response has the expected structure
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch controllers');
      }
      
      // Return data with pagination info if available
      return {
        data: response.data || [],
        pagination: response.pagination
      };
    } catch (error) {
      throw error;
    }
  }

  static async getControllerById(id: string): Promise<Controller> {
    try {
      const response = await sdk.client.get(`/api/v1/controllers/${id}`);
      const result: ControllerResponse = response.data;
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch controller');
      }
      
      return result.data;
    } catch (error) {
      throw error;
    }
  }

  static async createController(data: CreateControllerData): Promise<Controller> {
    try {
      const response = await sdk.client.post('/api/v1/controllers', data);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to create controller');
      }
      
      return response.data;
    } catch (error: any) {
      
      // Extract specific error message from backend response
      if (error?.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error?.response?.status === 400) {
        throw new Error('Invalid data provided. Please check your input and try again.');
      } else if (error?.response?.status === 409) {
        throw new Error('A controller with this CNIC or phone number already exists.');
      } else {
        throw new Error(getErrorMessage(error, 'Failed to create controller. Please try again.'));
      }
    }
  }

  static async updateController(id: string, data: UpdateControllerData): Promise<Controller> {
    try {
      const response = await sdk.client.put(`/api/v1/controllers/${id}`, data);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to update controller');
      }
      
      return response.data;
    } catch (error: any) {
      
      // Extract specific error message from backend response
      if (error?.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error?.response?.status === 400) {
        throw new Error('Invalid data provided. Please check your input and try again.');
      } else if (error?.response?.status === 404) {
        throw new Error('Controller not found.');
      } else if (error?.response?.status === 409) {
        throw new Error('A controller with this CNIC or phone number already exists.');
      } else {
        throw new Error(getErrorMessage(error, 'Failed to update controller. Please try again.'));
      }
    }
  }

  static async deleteController(id: string): Promise<void> {
    try {
      const response = await sdk.client.delete(`/api/v1/controllers/${id}`);
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to delete controller');
      }
    } catch (error: any) {
      
      // Extract specific error message from backend response
      if (error?.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else if (error?.response?.status === 400) {
        throw new Error('Cannot delete controller. Controller is assigned to regions or offices. Please reassign them first.');
      } else if (error?.response?.status === 404) {
        throw new Error('Controller not found.');
      } else {
        throw new Error(getErrorMessage(error, 'Failed to delete controller. Please try again.'));
      }
    }
  }

  static async searchControllers(query: string): Promise<Controller[]> {
    try {
      const response = await sdk.client.get(`/api/v1/controllers/search?q=${encodeURIComponent(query)}`);
      const result: ControllersResponse = response.data;
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to search controllers');
      }
      
      return result.data;
    } catch (error) {
      throw error;
    }
  }
}
